"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MonitorAlertSchema = exports.MonitorSchema = exports.validateMonitorQuery = exports.validateAtLeastOneTrigger = exports.validateThresholdOrder = exports.MonitorNoDataSchema = exports.MonitorNoDataModeSchema = exports.MonitorRenotifySchema = exports.MonitorViewSchema = exports.windowFromMs = exports.windowToMs = exports.MonitorWindowSchema = exports.MonitorThresholdOperatorSchema = exports.MonitorWriteStatusSchema = exports.MonitorStatusSchema = exports.MonitorSeveritySchema = exports.MonitorFiltersSchema = exports.ErrorAtLeastOneTrigger = exports.ErrorAlertThresholdRequired = exports.ErrorNameRequired = exports.monitorEvaluationOffsetMs = void 0;
/** types.ts contains the monitor domain — schemas, types, and the
 * `validate*` refinements consumed by the input schemas. */
const client_1 = require("@prisma/client");
const zod_1 = require("zod");
const InvalidRequestError_1 = require("../../errors/InvalidRequestError");
const filters_1 = require("../../interfaces/filters");
const types_1 = require("../query/types");
const isValidQuery_1 = require("./isValidQuery");
const isValidThresholdOrder_1 = require("./isValidThresholdOrder");
/** monitorEvaluationOffsetMs shifts the query window back so ClickHouse reads settled data past the events-table write lag. */
exports.monitorEvaluationOffsetMs = 30 * 1000;
/** ErrorNameRequired is the message emitted when the Monitor name is missing or empty. */
exports.ErrorNameRequired = "Name is a required field";
/** ErrorAlertThresholdRequired is the message emitted when alertThreshold is missing. */
exports.ErrorAlertThresholdRequired = "Alert threshold is a required field";
/** ErrorAtLeastOneTrigger is the message emitted when a Monitor has no automations. */
exports.ErrorAtLeastOneTrigger = "At least one automation is required";
/**
 * MonitorFiltersSchema is the array of filters applied to a Monitor's
 * underlying query. It routes through `singleFilterList` so the scheduler's
 * raw-SQL read path (which bypasses `monitorFromPrisma`) still coerces legacy
 * empty-substring metadata filters to `is set` before the queue event is
 * validated in the worker — otherwise a single legacy filter throws on parse
 * and takes down the whole shared scheduler batch.
 */
exports.MonitorFiltersSchema = filters_1.singleFilterList;
/** MonitorSeveritySchema is the wire form of Prisma's `MonitorSeverity` enum. */
exports.MonitorSeveritySchema = zod_1.z.enum(client_1.MonitorSeverity);
/** MonitorStatusSchema is the wire form of Prisma's `MonitorStatus` enum. */
exports.MonitorStatusSchema = zod_1.z.enum(client_1.MonitorStatus);
/** MonitorWriteStatusSchema is the subset of MonitorStatusSchema callers may submit on create/update. */
exports.MonitorWriteStatusSchema = zod_1.z.enum([
    client_1.MonitorStatus.ACTIVE,
    client_1.MonitorStatus.PAUSED,
]);
/** MonitorThresholdOperatorSchema is the wire form of Prisma's `MonitorThresholdOperator` enum. */
exports.MonitorThresholdOperatorSchema = zod_1.z.enum(client_1.MonitorThresholdOperator);
/**
 * MonitorWindowSchema is the kebab-case wire form of a Monitor evaluation
 * window. The service translates between this and a bigint of milliseconds
 * (the Prisma `windowMs` column) at the persistence boundary.
 */
exports.MonitorWindowSchema = types_1.granularities.extract([
    "5m",
    "10m",
    "15m",
    "30m",
    "1h",
    "2h",
    "4h",
    "1d",
    "2d",
    "1w",
]);
/** windowToMs converts the MonitorWindow api enum to a bigint of milliseconds. */
const windowToMs = (w) => {
    switch (w) {
        case "5m":
            return 5n * 60000n;
        case "10m":
            return 10n * 60000n;
        case "15m":
            return 15n * 60000n;
        case "30m":
            return 30n * 60000n;
        case "1h":
            return 60n * 60000n;
        case "2h":
            return 2n * 60n * 60000n;
        case "4h":
            return 4n * 60n * 60000n;
        case "1d":
            return 24n * 60n * 60000n;
        case "2d":
            return 2n * 24n * 60n * 60000n;
        case "1w":
            return 7n * 24n * 60n * 60000n;
    }
};
exports.windowToMs = windowToMs;
/** windowFromMs converts a bigint of milliseconds to the MonitorWindow api enum. */
const windowFromMs = (ms) => {
    switch (ms) {
        case 5n * 60000n:
            return "5m";
        case 10n * 60000n:
            return "10m";
        case 15n * 60000n:
            return "15m";
        case 30n * 60000n:
            return "30m";
        case 60n * 60000n:
            return "1h";
        case 2n * 60n * 60000n:
            return "2h";
        case 4n * 60n * 60000n:
            return "4h";
        case 24n * 60n * 60000n:
            return "1d";
        case 2n * 24n * 60n * 60000n:
            return "2d";
        case 7n * 24n * 60n * 60000n:
            return "1w";
        default:
            throw new InvalidRequestError_1.InvalidRequestError(`windowMs ${ms.toString()} does not correspond to a known MonitorWindow tier`);
    }
};
exports.windowFromMs = windowFromMs;
/**
 * MonitorViewSchema is an alias of the query `viewsV2` schema.
 */
exports.MonitorViewSchema = types_1.viewsV2;
/**
 * MonitorRenotifySchema describes renotify behavior for a sustained severity.
 * `OFF` is edge-only; `EVERY` re-emits every `intervalMinutes` while the
 * severity persists.
 */
exports.MonitorRenotifySchema = zod_1.z.discriminatedUnion("mode", [
    zod_1.z.object({ mode: zod_1.z.literal("OFF") }),
    zod_1.z.object({
        mode: zod_1.z.literal("EVERY"),
        intervalMinutes: zod_1.z
            .number()
            .int()
            .min(1)
            .max(60 * 24 * 7),
    }),
]);
/** MonitorNoDataModeSchema enumerates how a null metric value resolves to a severity. */
exports.MonitorNoDataModeSchema = zod_1.z.enum([
    "SUBSTITUTE_ZERO",
    "LAST_SEVERITY",
    "SHOW_NO_DATA",
    "NOTIFY_NO_DATA",
]);
/** MonitorNoDataSchema describes how a null metric value resolves to a severity. */
exports.MonitorNoDataSchema = zod_1.z.discriminatedUnion("mode", [
    zod_1.z.object({ mode: zod_1.z.literal(exports.MonitorNoDataModeSchema.enum.SUBSTITUTE_ZERO) }),
    zod_1.z.object({ mode: zod_1.z.literal(exports.MonitorNoDataModeSchema.enum.LAST_SEVERITY) }),
    zod_1.z.object({ mode: zod_1.z.literal(exports.MonitorNoDataModeSchema.enum.SHOW_NO_DATA) }),
    zod_1.z.object({
        mode: zod_1.z.literal(exports.MonitorNoDataModeSchema.enum.NOTIFY_NO_DATA),
        intervalMinutes: zod_1.z
            .number()
            .int()
            .min(1)
            .max(60 * 24),
    }),
]);
/** validateThresholdOrder enforces correct warning/alert threshold ordering on the Monitor input schemas. */
const validateThresholdOrder = (input, ctx) => {
    const result = (0, isValidThresholdOrder_1.isValidThresholdOrder)(input);
    if (!result.valid) {
        ctx.addIssue({
            code: "custom",
            message: result.reason,
            path: ["threshold"],
        });
    }
};
exports.validateThresholdOrder = validateThresholdOrder;
/** validateAtLeastOneTrigger enforces that a Monitor has at least one automation on the input schemas. */
const validateAtLeastOneTrigger = (input, ctx) => {
    if (input.triggerIds.length < 1) {
        ctx.addIssue({
            code: "custom",
            message: exports.ErrorAtLeastOneTrigger,
            path: ["triggerIds"],
        });
    }
};
exports.validateAtLeastOneTrigger = validateAtLeastOneTrigger;
/**
 * validateMonitorQuery enforces a valid (view, metric, filters) shape on the
 * Monitor input schemas.
 */
const validateMonitorQuery = (input, ctx) => {
    const result = (0, isValidQuery_1.isValidQuery)({
        view: input.view,
        metrics: [input.metric],
        filters: input.filters,
    });
    if (!result.valid) {
        ctx.addIssue({
            code: "custom",
            message: result.reason,
            path: ["query"],
        });
    }
};
exports.validateMonitorQuery = validateMonitorQuery;
/**
 * MonitorSchema is the Monitor domain object. It mirrors the Prisma `Monitor`
 * row.
 */
exports.MonitorSchema = zod_1.z.object({
    id: zod_1.z.string(),
    createdAt: zod_1.z.date(),
    updatedAt: zod_1.z.date(),
    createdBy: zod_1.z.string().nullable(),
    updatedBy: zod_1.z.string().nullable(),
    projectId: zod_1.z.string(),
    // Query Config
    view: exports.MonitorViewSchema,
    filters: exports.MonitorFiltersSchema,
    metric: types_1.metric,
    // Monitor Config
    window: exports.MonitorWindowSchema,
    thresholdOperator: exports.MonitorThresholdOperatorSchema,
    alertThreshold: zod_1.z.number({ message: exports.ErrorAlertThresholdRequired }),
    warningThreshold: zod_1.z.number().nullable(),
    noData: exports.MonitorNoDataSchema.default({
        mode: exports.MonitorNoDataModeSchema.enum.SUBSTITUTE_ZERO,
    }),
    renotify: exports.MonitorRenotifySchema.default({ mode: "OFF" }),
    // MonitorAlert Config
    name: zod_1.z
        .string({ message: exports.ErrorNameRequired })
        .min(1, exports.ErrorNameRequired)
        .max(200),
    tags: zod_1.z.array(zod_1.z.string().max(60)).max(20).default([]),
    triggerIds: zod_1.z.array(zod_1.z.string()).default([]),
    // Monitor State
    severity: exports.MonitorSeveritySchema.default("UNKNOWN"),
    severityChangedAt: zod_1.z.date().nullable(),
    alertedAt: zod_1.z.date().nullable(),
    // MonitorScheduler State
    status: exports.MonitorStatusSchema.default("ACTIVE"),
    nextRunAt: zod_1.z.date().nullable(),
    lastPublishedAt: zod_1.z.date().nullable(),
    lastClaimedAt: zod_1.z.date().nullable(),
    lastCompletedAt: zod_1.z.date().nullable(),
});
/**
 * MonitorAlertSchema is emitted when a monitor alerts.
 * It carries enough information to query events that occurred
 * during the evaluation window of the monitor.
 */
exports.MonitorAlertSchema = zod_1.z.object({
    monitorId: zod_1.z.string(),
    projectId: zod_1.z.string(),
    permalink: zod_1.z.url().optional(),
    /** dataPermalink deep-links to the data table scoped to the breaching window. Optional so in-flight webhook messages without it still parse. */
    dataPermalink: zod_1.z.url().optional(),
    message: zod_1.z.object({ title: zod_1.z.string(), body: zod_1.z.string() }),
    severity: exports.MonitorSeveritySchema,
    timestamp: zod_1.z.coerce.date(),
    fromTimestamp: zod_1.z.coerce.date(),
    toTimestamp: zod_1.z.coerce.date(),
    view: exports.MonitorViewSchema,
    filters: exports.MonitorFiltersSchema,
    window: exports.MonitorWindowSchema,
    monitorName: zod_1.z.string().optional(),
    metric: types_1.metric.optional(),
    metricValue: zod_1.z.number().nullable().optional(),
    thresholdOperator: exports.MonitorThresholdOperatorSchema.optional(),
    alertThreshold: zod_1.z.number().optional(),
    warningThreshold: zod_1.z.number().nullable().optional(),
    alertedAt: zod_1.z.coerce.date().optional(),
});
